import { useState } from 'react';
import * as Yup from 'yup';

import {
  EDUCATION,
  EDUCATION_SCHOOL,
  EDUCATION_DEGREE,
  CURRENT,
  START_DATE,
  END_DATE,
  DESCRIPTION,
  EDUCATION_MAJOR,
} from '@zr/utilities/profileFields';

import {
  YYYYtoIso8061,
  iso8061ToYYYY,
} from '@zr/utilities/date';

import { valEndYearAfterStartYear } from '@zr/utilities/form/validators';

import {
  STRING_FIELD_EDUCATION_SCHOOL_REQUIRED,
  STRING_FIELD_EDUCATION_DEGREE_REQUIRED,
  STRING_FIELD_END_BEFORE_START_DATE,
} from '@zr/components/Fields/labels';

import {
  STRING_EDUCATION,
  STRING_OF,
} from '../labels';

import handleUpdateProfileFormikSubmit from './handleUpdateProfileFormikSubmit';

export const minimalValidationSchema = Yup.object().shape({
  [EDUCATION_SCHOOL]: Yup.string().trim().required(STRING_FIELD_EDUCATION_SCHOOL_REQUIRED),
  [EDUCATION_DEGREE]: Yup.string().trim().required(STRING_FIELD_EDUCATION_DEGREE_REQUIRED),
});

// eslint-disable-next-line arrow-body-style
export const validationSchema = () => Yup.lazy((values) => {
  return Yup.object({
    [EDUCATION_SCHOOL]: Yup.string().trim().required(STRING_FIELD_EDUCATION_SCHOOL_REQUIRED),
    [EDUCATION_DEGREE]: Yup.string().trim().required(STRING_FIELD_EDUCATION_DEGREE_REQUIRED),
    [END_DATE]: Yup.string().test(
      'end_date_is_before_start_date_year',
      STRING_FIELD_END_BEFORE_START_DATE,
      () => (
        valEndYearAfterStartYear(
          values[START_DATE],
          values[END_DATE],
        )
      ),
    ),
  });
});

// this is never prefilled (but maybe in the future if re-used)
export const initialValues = {
  [EDUCATION_SCHOOL]: '',
  [EDUCATION_DEGREE]: '',
  [EDUCATION_MAJOR]: '',
  [CURRENT]: false,
  [END_DATE]: '',
  [DESCRIPTION]: '',
};

/**
 * Transforms education form values into an object structured for patching profile
 * Note this is a single entry, the actual profile API accepts arrays only, see transformEducationFormToPatchData
 *
 * @param {object} formData education form data
 * @returns patchEntry transformed education object structured as an entry for patching
 */
export const transformEducationFormToPatchEntry = (
  formData,
) => {
  // remove fields with empty strings
  const educationEntry = Object.entries(formData).filter(
    ([, value]) => (value !== ''),
  ).reduce((newEntry, [key, value]) => ({
    ...newEntry,
    [key]: value,
  }), {});

  // if end date, convert the form number value into an Iso8061 date string and add precision
  if (educationEntry[END_DATE]) {
    educationEntry[END_DATE] = YYYYtoIso8061(educationEntry[END_DATE]);
    educationEntry[`${END_DATE}_precision`] = 'Year';
  }

  return educationEntry;
};

/**
 * Transforms education form values and adds them to an array of experiences ready for patching the profile with
 *
 * @param {object} formData education form data
 * @param {object} previousEducationEntries rest of jobseeker's education required to correctly update their profile with
 * @returns {object} patchData
 */
export const transformEducationFormToPatchData = (
  formData, previousEducationEntries = { [EDUCATION]: [] },
) => {
  const educationEntry = transformEducationFormToPatchEntry(formData);

  return {
    [EDUCATION]: [
      ...previousEducationEntries[EDUCATION],
      educationEntry,
    ],
  };
};

/**
 * Transforms server profile jso structured education entry into education form values
 *
 * @param {object} serverEntry single education entry structured from the server
 * @returns formValues
 */
export const transformEducationServerEntryToFormData = (serverEntry) => {
  const endDate = iso8061ToYYYY(serverEntry[END_DATE]);
  const startDate = iso8061ToYYYY(serverEntry[START_DATE]);

  const formData = {
    // API can return fields as null or empty string, or leave the field absent
    // handle them all as empty string
    [EDUCATION_SCHOOL]: serverEntry[EDUCATION_SCHOOL] || '',
    [EDUCATION_DEGREE]: serverEntry[EDUCATION_DEGREE] || '',
    [EDUCATION_MAJOR]: serverEntry[EDUCATION_MAJOR] || '',
    [CURRENT]: serverEntry[CURRENT],
    [START_DATE]: startDate,
    [END_DATE]: endDate,
    [DESCRIPTION]: serverEntry[DESCRIPTION] || '',
    // server index
    _idx: serverEntry.idx,
  };

  return formData;
};

/**
 * Formik submit handler for updating education and handles add another/no more entries logic
 * @param {object} addAnotherRef react ref containing the add another/no more entries flag
 * @param {string} options.method used in profile_method
 * @param {function} options.onAddAnother callback ran when successfully updated with add another specified
 * @param {function} options.onNoMore callback ran when successfully updated with no more entries specified
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onStartSubmit callback ran at the start, useful for clearing previous errors
 * @returns formik submit handler
 */
export const useEducationFormikSubmit = (
  addAnotherRef,
  setIsLoading = () => {},
  {
    method,
    onAddAnother = () => {},
    onNoMore,
    onError,
    onStartSubmit,
  },
) => {
  const [educations, setEducations] = useState({
    [EDUCATION]: [],
  });

  const handleFormSubmit = async (data, formik) => {
    const nextEducations = transformEducationFormToPatchData(data, educations);

    const handleFormSuccess = async () => {
      if (addAnotherRef.current) {
        const { resetForm } = formik;
        setEducations(nextEducations);
        // reset the form
        resetForm({
          values: initialValues,
        });
        onAddAnother();
      } else {
        // callback could be async (might call apply endpoint), await keeps isSubmitting true during that
        await onNoMore();
      }
    };

    // when the profile api returns a validation error for education, get the actual entry we're validating
    const transformValidationFieldsStatus = (fields) => {
      // assume we're validating the last entry, may not be true if we use this more
      const index = nextEducations[EDUCATION].length - 1;
      const invalidEntry = fields[EDUCATION]?.find(({ _idx }) => (
        _idx === index
      ));
      return invalidEntry || fields;
    };

    // await needed to make formik isSubmitting to stay true while this is going on
    await handleUpdateProfileFormikSubmit({
      method,
      onError,
      onStart: onStartSubmit,
      onSuccess: handleFormSuccess,
      transformValidationFieldsStatus,
      setIsLoading,
    })(nextEducations, formik);
  };

  const numEducationsAdded = educations[EDUCATION].length;

  return [numEducationsAdded, handleFormSubmit];
};

export function getLegendText(entryCount) {
  // if submitted an entry, start showing the count
  if (entryCount) {
    const entryDisplay = `${entryCount + 1} ${STRING_OF} ${entryCount + 1}`;
    return `${STRING_EDUCATION} ${entryDisplay}`;
  }
  return STRING_EDUCATION;
}

/**
 * Crafts a validate_only education formik submit handler
 * @param {object} options.formData education form data
 * @param {object} options.formikProps formik props provided by formik
 * @param {string} options.method used in profile_method
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onStartSubmit callback ran at the start, useful for clearing previous errors
 * @param {function} options.onSuccess callback ran when successfully validated
 * @param {function} options.onValidationError callback ran when running into a validation_error
 * @returns {function} formik submit handler
 */
export async function educationFormikValidateOnlySubmit({
  formData,
  formikProps,
  method,
  onError,
  onStartSubmit,
  onSuccess,
  onValidationError,
}) {
  // when the profile api returns a validation error for education, get the actual entry we're validating
  const transformValidationFieldsStatus = (fields) => (
    fields[EDUCATION]?.[0] ?? fields
  );

  return handleUpdateProfileFormikSubmit({
    method,
    onError,
    onStart: onStartSubmit,
    onSuccess,
    onValidationError,
    transformValidationFieldsStatus,
    validateOnly: true,
  })(transformEducationFormToPatchData(formData), formikProps);
}
